import {Component, OnInit, ViewChild} from '@angular/core';
import {Line} from '@app/shared/models/line.model';
import {ToolRequest} from '@app/shared/models/toolRequests.model';
import {TableConfig} from '@app/shared/util/table-config.utils';
import {Tool} from '@app/tool/model/tool.model';
import {ToolSet} from '@app/tool/model/toolSet.model';
import {ToolRequestsService} from '@app/core/services/tool-requests.service';
import {Log} from 'ng2-logger';
import {ConfirmationModalComponent} from '@app/shared/components';
import {TranslateService} from '@ngx-translate/core';
import {ToolReturnsService} from '@app/core/services/tool-returns.service';
import {ToolReturnToolsFilterModel} from "@models/tool-return-tools-filter.model";
import { LogoutModalComponent } from '@app/shared/components';
import { environment } from '../../../../../environments/environment';

enum ToolReturnSteps {
    SELECT_LINE_AND_TOOL_REQUEST = 0,
    CONFIRM = 1
}

@Component({
    selector: 'app-tool-return-for-maintenance',
    templateUrl: './tool-return-for-maintenance.component.html',
    styleUrls: ['./tool-return-for-maintenance.component.scss']
})
export class ToolReturnForMaintenanceComponent implements OnInit {

    _logger = Log.create(ToolReturnForMaintenanceComponent.name);

    @ViewChild('successModal', {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;
    @ViewChild(LogoutModalComponent, {static: false}) logoutModal: LogoutModalComponent;

    currentStep = 1;
    firstNextTried = false;
    selectedLine: Line;
    isLoading;

    toolsNeededToBeReturnedLoading = false;
    toolsNeededAtLineLoading = false;
    toolsExcludedFromMaintenanceLoading = false;

    selectedToolRequest: ToolRequest;

    toolsNeededAtLine: Array<Tool | ToolSet> = [];
    toolsNeededToBeReturned: Array<Tool | ToolSet> = [];
    toolsExcludedFromMaintenance: Array<Tool | ToolSet> = [];
    confirmationPopupMessage: string;
    isConfirmationInProgress = false;
    readonly badgeLogoutCountdownSeconds = environment.badgeLogoutCountdownSeconds;

    constructor(private translateService: TranslateService,
                private toolReturnService: ToolReturnsService,
                private toolRequestService: ToolRequestsService) {
    }

    ngOnInit() {
    }


    onLineChanged(line: Line) {

        this.selectedToolRequest = null;
        if (line && line instanceof Line) {
            this.selectedLine = line;
        }
    }

    onToolRequestChanged(toolRequest: ToolRequest) {
        if (toolRequest && toolRequest instanceof ToolRequest) {
            this.selectedToolRequest = toolRequest;

            this.toolsNeededAtLine = [];
            this.toolsNeededToBeReturned = [];
            this.toolsExcludedFromMaintenance = [];

            const tableConfig = new TableConfig();
            tableConfig.pager.pageSize =  -1;
            this.getDetailsForToolRequest(this.selectedToolRequest, tableConfig);
        } else {
            this.selectedToolRequest = null;
        }
    }

    onStepChange(stepIndex) {
        this.firstNextTried = false;
        switch (stepIndex) {
            case ToolReturnSteps.SELECT_LINE_AND_TOOL_REQUEST:
                this.currentStep = 1;
                break;
            case ToolReturnSteps.CONFIRM:
                this.currentStep = 2;
                break;
        }
    }

    onTryNext() {
        this.firstNextTried = true;
    }

    onConfirmClick() {
        this.confirmationModal.open();
    }

    onConfirmReturn(reason: string) {
        this.confirmationPopupMessage = '';
        this.isConfirmationInProgress = true;
        if (this.selectedToolRequest && this.selectedLine) {
            const toolReturn = {
                lineId: this.selectedLine.id,
                toolRequestId: this.selectedToolRequest.id,
                toolIds: this.getIdsArrayForSelectedTools(this.toolsNeededToBeReturned),
                excludedToolIds: this.getIdsArrayForSelectedTools(this.toolsExcludedFromMaintenance),
                reason: reason
            };
            this._logger.info("toolReturn:", toolReturn);

            this.toolReturnService.createToolReturnForMaintenance(toolReturn).subscribe(
                (data) => {
                    this.confirmationModal.close();
                    this._logger.info("getTollRequestsForLine:: API Returned data", data);
                    this.confirmationPopupMessage = this.buildCreateMessage(data.id);
                    this.isConfirmationInProgress = false;
                    this.logoutModal.open();
                },
                (error) => {
                    this.confirmationModal.close();
                    if (error && error.error) {
                        this.confirmationPopupMessage = this.translateService.instant(error.error.message);
                        this.errorModal.open();
                    }
                    this._logger.info("getToolRequestsForLine:: API Returned error", error);
                    this.isConfirmationInProgress = false;
                }
            )
        }
    }

    private getIdsArrayForSelectedTools(selectedTools: (Tool | ToolSet)[]) {
        const ids = [];
        for (const toolItem of selectedTools) {
            if (toolItem instanceof ToolSet) {
                for (const tool of toolItem.tools) {
                    ids.push(tool.id);
                }
            } else {
                ids.push(toolItem.id);
            }
        }
        return ids;
    }

    private buildCreateMessage(toolReturnRequestName) {
        return this.translateService.instant('lineSetter.tool-returns.toasts.created',
            {toolReturnRequestName: toolReturnRequestName});
    }


    private getDetailsForToolRequest(toolRequest: ToolRequest, tableConfig: TableConfig) {
        this._logger.info("getDetailsForToolRequest:: Retrieving Details for Tool Request for maintenance", toolRequest);

        const toolReturnToolsFilter = new ToolReturnToolsFilterModel();
        toolReturnToolsFilter.excludedFromMaintenance = false;

        toolReturnToolsFilter.hasMaintenanceAtLine = false;
        this.toolsNeededToBeReturnedLoading = true;
        this.toolRequestService.getToolReturnRequestsByToolRequest(toolRequest, tableConfig, toolReturnToolsFilter).subscribe(
            (data) => {
                this._logger.info("getDetailsForToolRequest:: API Returned data", data);
                this.toolsNeededToBeReturned = data.items;
                this.toolsNeededToBeReturnedLoading = false;
            },
            (error) => {
                this._logger.info("getDetailsForToolRequest:: API Returned error", error);
            }
        );

        toolReturnToolsFilter.hasMaintenanceAtLine = true;
        this.toolsNeededAtLineLoading = true;
        this.toolRequestService.getToolReturnRequestsByToolRequest(toolRequest, tableConfig, toolReturnToolsFilter).subscribe(
            (data) => {
                this._logger.info("getDetailsForToolRequest:: API Returned data", data);
                this.toolsNeededAtLine = data.items;
                this.toolsNeededAtLineLoading = false;
            },
            (error) => {
                this._logger.info("getDetailsForToolRequest:: API Returned error", error);
            }
        )

        toolReturnToolsFilter.excludedFromMaintenance = true;
        toolReturnToolsFilter.hasMaintenanceAtLine = null;
        this.toolsExcludedFromMaintenanceLoading = true;
        this.toolRequestService.getToolReturnRequestsByToolRequest(toolRequest, tableConfig, toolReturnToolsFilter).subscribe(
            (data) => {
                this._logger.info("getDetailsForToolRequest:: API Returned data", data);
                this.toolsExcludedFromMaintenance = data.items;
                this.toolsExcludedFromMaintenanceLoading = false;
            },
            (error) => {
                this._logger.info("getDetailsForToolRequest:: API Returned error", error);
            }
        )
    }

    get toolsLoading() {
        return this.toolsNeededToBeReturnedLoading || this.toolsNeededAtLineLoading || this.toolsExcludedFromMaintenanceLoading;
    }
}
